import React from "react";

const lastUpdated = "December 6, 2025";

const Section = ({ id, title, children }) => (
  <section id={id} className="legal-section">
    <h2>{title}</h2>
    {children}
  </section>
);

export default function TermsOfService() {
  return (
    <article className="legal-card" aria-label="ClearBadge™ Terms of Service">
      <p className="legal-updated">Last updated: {lastUpdated}</p>
      <p>
        These Terms of Service ("Terms") govern your access to and use of ClearBadge™'s websites, applications, public
        directory, profiles, and other online products or services (collectively, the "Services").
      </p>
      <br></br>
      <p>By accessing or using
        the Services, you agree to be bound by these Terms. If you do not agree, do not use the Services.</p>
      {/* <p className="legal-note">These Terms are provided for general informational purposes and are not legal advice.</p> */}

      <Section id="acceptance" title="1. Acceptance of Terms">
        <p>
          You must be able to form a binding contract to use the Services. By using the Services, you represent that you
          are at least 18 years old (or the age of majority in your jurisdiction) and have the authority to accept these
          Terms on behalf of yourself or the entity you represent.
        </p>
      </Section>

      <Section id="services" title="2. Description of the Services">
        <p>
          ClearBadge™ operates an online directory and verification platform that allows consumers to search for licensed
          professionals, and allows professionals to claim or manage profiles, display digital business cards, and
          purchase subscription-based enhancements. We may update, modify, or discontinue the Services at any time with or
          without notice.
        </p>
      </Section>

      <Section id="eligibility" title="3. Eligibility, Accounts, and Security">
        <ul className="legal-list">
          <li>To claim a profile or purchase a subscription, you must create an account and provide accurate, complete information.</li>
          <li>You are responsible for safeguarding your login credentials and for all activity that occurs under your account.</li>
          <li>You agree to notify us immediately of any unauthorized use of your account or suspected security breach.</li>
        </ul>
      </Section>

      <Section id="listings" title="4. Directory Listings and Verification">
        <p>
          Public directory listings may be created from public licensing sources and are labeled as "ClearBadge™ Verified" or
          "Not ClearBadge™ Verified." Claiming a listing allows you to submit additional information and documentation for
          review. ClearBadge™ may update, correct, or remove content to reflect licensing authority data or to comply with
          applicable laws.
        </p>
        <p>
          Providing documentation does not guarantee verification. ClearBadge™ may rely on third-party databases and is not
          responsible for inaccuracies originating from those sources. It remains your responsibility to ensure that
          information displayed on your profile is accurate and current.
        </p>
      </Section>

      <Section id="payments" title="5. Subscriptions, Payments, and Renewals">
        <ul className="legal-list">
          <li>Paid Services, including subscription tiers and digital business card upgrades, are billed through authorized third-party payment processors.</li>
          <li>You authorize us and our processors to charge the payment method on file for all applicable fees and taxes.</li>
          <li>Unless otherwise stated, subscriptions renew automatically at the end of each billing cycle until canceled.</li>
          <li>Fees are non-refundable except as required by law or expressly stated in writing by ClearBadge™.</li>
        </ul>
      </Section>

      <Section id="acceptable-use" title="6. Acceptable Use">
        <p>You agree not to engage in any of the following:</p>
        <ul className="legal-list">
          <li>Use the Services for unlawful, fraudulent, or unauthorized purposes.</li>
          <li>Misrepresent your identity, professional license, or affiliation.</li>
          <li>Scrape, crawl, harvest, or collect information from the Services by automated means without permission.</li>
          <li>Interfere with or disrupt the integrity or performance of the Services.</li>
          <li>Upload malicious code, spam, or content that infringes, misappropriates, or violates any rights of third parties.</li>
        </ul>
      </Section>

      <Section id="ip" title="7. Intellectual Property">
        <p>
          ClearBadge™ owns all rights, title, and interest in the Services, including all software, logos, and content, except
          for user-submitted materials and third-party content. You may not copy, modify, or create derivative works based
          on the Services without our prior written consent. By submitting content, you grant ClearBadge™ a worldwide,
          non-exclusive, royalty-free license to use, reproduce, modify, and display that content in connection with the
          Services.
        </p>
      </Section>

      <Section id="disclaimer" title="8. Disclaimers; No Professional Advice">
        <p>
          The Services are provided on an "as is" and "as available" basis. ClearBadge™ disclaims all warranties, express or
          implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          Search results and verification statuses are provided for informational purposes only and do not constitute legal,
          financial, or professional advice.
        </p>
      </Section>

      <Section id="liability" title="9. Limitation of Liability">
        <p>
          To the fullest extent permitted by law, ClearBadge™ will not be liable for any indirect, incidental, special,
          consequential, or punitive damages, or for any loss of profits or revenues, whether incurred directly or
          indirectly, or any loss of data, use, goodwill, or other intangible losses. Our total liability for any claim
          arising out of or relating to the Services will not exceed the amount you paid to ClearBadge™ in the twelve (12)
          months immediately preceding the event giving rise to the claim.
        </p>
      </Section>

      <Section id="indemnity" title="10. Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless ClearBadge™, its affiliates, and their respective officers,
          directors, employees, and agents from and against any claims, damages, liabilities, losses, and expenses
          (including attorneys' fees) arising out of or relating to your use of the Services, your content, or your
          violation of these Terms or any applicable law.
        </p>
      </Section>

      <Section id="termination" title="11. Termination and Suspension">
        <p>
          We may suspend or terminate your access to the Services at any time if we believe you have violated these Terms,
          pose a security risk, or engage in fraudulent/abusive behavior. You may cancel your account at any time. Sections
          that by their nature should survive termination (e.g., intellectual property, disclaimers, limitations of
          liability, and indemnity) will continue to apply.
        </p>
      </Section>

      <Section id="law" title="12. Governing Law and Dispute Resolution">
        <p>
          These Terms are governed by the laws of the State of Florida, without regard to its conflict of laws rules.
          Except where prohibited, any disputes arising under these Terms will be resolved exclusively in the state or
          federal courts located in Miami-Dade County, Florida, and you consent to the jurisdiction of those courts.
        </p>
      </Section>

      <Section id="changes" title="13. Changes to These Terms">
        <p>
          We may update these Terms from time to time. When we do, we will revise the "Last updated" date at the top of
          this page. If the changes are material, we may provide additional notice, such as by email or by posting a
          prominent notice on the Services. Your continued use of the Services after the revised Terms become effective
          constitutes your acceptance of the changes.
        </p>
      </Section>

      <Section id="contact" title="14. Contact Us">
        <p>
          If you have questions about these Terms or the Services, please contact us at support@clearbadge.org
        </p>
      </Section>
    </article>
  );
}


